import { useCallback, useEffect, useMemo, useState } from "react"
import { listAccounts, createAccount, SipRegistrarError } from "./services/sipRegistrar.api"
import { Search, UserPlus, Phone, Video, Contact, X } from "lucide-react"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/clientWeb/components/ui/table"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/clientWeb/components/ui/dialog"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectWrapper } from "@/clientWeb/components/ui/select"
import { Button } from "@/clientWeb/components/ui/button"
import { Input } from "@/clientWeb/components/ui/input"
import { Label } from "@/clientWeb/components/ui/label"

/**
 * Address Book — the directory of SIP/H.323 endpoints that may register with
 * this gateway.
 *
 * An endpoint can only register if an account already exists for it: the
 * gateway looks the username up, and the registration succeeds only when the
 * password also matches. Each row below is one such account, plus whether that
 * endpoint is checking in right now.
 *
 * Rows are real: the list is the gateway's provisioned accounts with live
 * registration state merged in, and "Add User" provisions an account the
 * endpoint can then register against. Everything goes through the backend
 * proxy at /api/v1/sip-registrar so the gateway key stays server-side.
 *
 * Protocol is real and per-row. One endpoint may serve both — the same device
 * with the same credentials — so the gateway records a *set* of protocols on
 * one account, and this list shows **one row per protocol**: searching "176"
 * returns its SIP row and its H.323 row separately, because that is how you
 * dial it. Adding an existing username for the other protocol therefore adds a
 * row rather than being refused; only an exact username+protocol repeat is.
 *
 * Online/IP come from the SIP registrar, so an H.323 row reads Offline with no
 * address until the H.323 gateway's own registrations are merged in too.
 *
 * Styling follows the other settings pages rather than inventing its own:
 * the page adds no outer padding (SettingsLayout already applies
 * `max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 md:py-6`), the card is
 * Telepresence's `rounded-2xl` + `shadow-xs` + `border-stroke-100`, the search
 * row and segmented filters are MyFiles', and the status pill and table header
 * are Telepresence's.
 */

type Protocol = "sip" | "h323"

interface Endpoint {
  id: string
  /** Display name, shown as the first line. */
  name: string
  /** The SIP/H.323 user the endpoint authenticates as. */
  username: string
  /** This row's protocol. An account serving both produces two rows. */
  protocol: Protocol
  /** Where the endpoint was last seen registering from. */
  ipAddress: string
  online: boolean
}

interface AddForm {
  protocol: Protocol
  name: string
  username: string
  password: string
  /**
   * SIP-ENC-P4: does this endpoint require encrypted media? Recorded per
   * endpoint because it is a fact about the DEVICE, not about one call, and
   * it cannot be discovered from a failure: PeopleLink refuses a plaintext
   * offer with 486 Busy Here, indistinguishable from genuinely busy.
   */
  encryption: 'required' | 'none'
}

const EMPTY_FORM: AddForm = {
  protocol: "sip", name: "", username: "", password: "", encryption: "none",
}

type ProtocolFilter = "all" | Protocol
type StatusFilter = "all" | "online" | "offline"

/** One slot per input, plus one for refusals that belong to neither. */
interface FormErrors {
  username?: string
  password?: string
  form?: string
}

/**
 * Put a gateway refusal under the field it is actually about.
 *
 * The gateway owns the rules — username shape, minimum secret length — and its
 * wording is shown verbatim rather than second-guessed here. But it answers
 * with one flat string and no field name, so the field has to be read off the
 * message: it calls the password a "secret" and the username a "user". A
 * protocol refusal, or the gateway being unreachable, belongs to neither input
 * and surfaces above the buttons instead.
 *
 * Password is tested first because it is the specific case; "user" appears in
 * far more sentences than "secret" does.
 *
 * Before this, EVERY refusal was written to `errors.username`, so
 * "secret required, min 12 chars" rendered under Username / Extension — the one
 * field it has nothing to say about.
 */
function errorForMessage(message: string): FormErrors {
  if (/\b(secret|password)\b/i.test(message)) return { password: message }
  if (/\buser(name)?\b/i.test(message)) return { username: message }
  return { form: message }
}

/** Segmented-control button, as used for the type filter in MyFiles. */
function SegButton({
  active, onClick, children,
}: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium transition-all ${
        active ? "bg-card shadow-sm text-primary" : "text-muted-foreground"
      }`}
    >
      {children}
    </button>
  )
}

/** Status pill, matching the active/disabled badge in Telepresence. */
function StatusBadge({ online }: { online: boolean }) {
  return (
    <span
      className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full ${
        online
          ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-400"
          : "bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-400"
      }`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${online ? "bg-emerald-500" : "bg-slate-400"}`} />
      {online ? "Online" : "Offline"}
    </span>
  )
}

export default function AddressBook() {
  const [endpoints, setEndpoints] = useState<Endpoint[]>([])
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [search, setSearch] = useState("")
  const [showAdd, setShowAdd] = useState(false)
  const [form, setForm] = useState<AddForm>(EMPTY_FORM)
  const [errors, setErrors] = useState<FormErrors>({})
  const [protocolFilter, setProtocolFilter] = useState<ProtocolFilter>("all")
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all")

  const onlineCount = endpoints.filter(e => e.online).length
  // Rows are per-protocol, so counting them would report one both-protocol
  // endpoint as two accounts. The username is the account.
  const accountCount = useMemo(
    () => new Set(endpoints.map(e => e.username.toLowerCase())).size,
    [endpoints],
  )

  const refresh = useCallback(async () => {
    try {
      const { accounts } = await listAccounts()
      // One row per protocol. The gateway keeps a single account per username —
      // one device, one secret — but the directory is read to answer "how do I
      // reach 176?", and that has a different answer per protocol.
      setEndpoints(accounts.flatMap((a) => {
        const protocols = (a.protocols?.length ? a.protocols : ["sip"]) as Protocol[]
        return protocols.map((protocol) => {
          // Each protocol has its own registration: SIP's comes from the SIP
          // registrar, H.323's from the gatekeeper. A device can be checked in
          // over one and not the other, so the row reports its own.
          const reg = protocol === "h323" ? a.h323 : { online: a.online, ipAddress: a.ipAddress }
          return {
            id: `${a.aor}:${protocol}`,
            name: a.username,
            username: a.username,
            protocol,
            ipAddress: reg?.ipAddress ?? "—",
            online: Boolean(reg?.online),
          }
        })
      }))
      setLoadError(null)
    } catch (err) {
      setLoadError(err instanceof SipRegistrarError ? err.message : "Could not load accounts")
    } finally {
      setLoading(false)
    }
  }, [])

  // Registrations expire in ~60s, so Online/Offline is only true for about a
  // minute. Re-read on the same cadence rather than letting the page go stale.
  useEffect(() => {
    void refresh()
    const id = setInterval(() => { void refresh() }, 30_000)
    return () => clearInterval(id)
  }, [refresh])

  /**
   * Add the account to the list.
   *
   * A username identifies one device, so it may not appear twice for the SAME
   * protocol. Adding it for the OTHER protocol is allowed and expected: the
   * gateway merges the two, and the row then reports both. Only an exact
   * username+protocol repeat is refused.
   *
   * A brand-new account has not registered yet, so it starts Offline with no
   * IP: the address is something the endpoint tells us when it checks in, not
   * something typed here.
   */
  const handleAdd = async () => {
    const username = form.username.trim()
    const password = form.password.trim()
    if (!username || !password || saving) return

    const clash = endpoints.find(
      e => e.username.toLowerCase() === username.toLowerCase() && e.protocol === form.protocol,
    )
    if (clash) {
      const label = form.protocol === "sip" ? "SIP" : "H.323"
      setErrors({ username: `"${username}" already has a ${label} account` })
      return
    }

    setSaving(true)
    try {
      await createAccount(
        username, password, form.protocol,
        // H.323 encryption is H.235.6 and not implemented, so only a SIP
        // account carries a policy; sending it for H.323 would record a
        // setting nothing honours.
        form.protocol === "sip" ? form.encryption : undefined,
      )
    } catch (err) {
      // The gateway owns the rules (username shape, minimum secret length), so
      // its refusal is shown verbatim instead of being guessed at here — but it
      // is shown under the field it is ABOUT. See errorForMessage.
      setErrors(errorForMessage(err instanceof SipRegistrarError ? err.message : "Could not create account"))
      setSaving(false)
      return
    }
    setSaving(false)

    // Re-read rather than appending a guess: the gateway decides the AoR, and
    // the new account is Offline until the endpoint actually registers.
    await refresh()

    // Show the row that was just created: a filter left on H.323, or a stale
    // search, would hide it and read as "nothing happened".
    setSearch("")
    setProtocolFilter("all")
    setStatusFilter("all")

    setShowAdd(false)
    setForm(EMPTY_FORM)
    setErrors({})
  }

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase()
    return endpoints.filter(e => {
      if (protocolFilter !== "all" && e.protocol !== protocolFilter) return false
      if (statusFilter === "online" && !e.online) return false
      if (statusFilter === "offline" && e.online) return false
      if (!q) return true
      // Every column someone might recognise an endpoint by.
      return (
        e.name.toLowerCase().includes(q) ||
        e.username.toLowerCase().includes(q) ||
        e.ipAddress.toLowerCase().includes(q)
      )
    })
  }, [endpoints, search, protocolFilter, statusFilter])

  return (
    <div className="space-y-5 animate-in fade-in slide-in-from-bottom-4 duration-500">

      {/* Header */}
      <div className="flex items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-slate-900 dark:text-foreground/80 dark:text-white">
            Address Book
          </h2>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            {loading
              ? "Loading accounts…"
              : loadError
                ? loadError
                : `${accountCount} accounts configured — ${onlineCount} online`}
          </p>
        </div>
        <button
          type="button"
          onClick={() => { setForm(EMPTY_FORM); setErrors({}); setShowAdd(true) }}
          className="flex items-center gap-2 bg-primary text-white text-sm font-semibold px-4 py-2 rounded-lg hover:bg-primary/90 transition-colors shrink-0"
        >
          <UserPlus className="w-4 h-4" />
          Add User
        </button>
      </div>

      {/* Card: search + filters + table */}
      <div className="bg-card border border-stroke-100 dark:border-border rounded-2xl shadow-xs overflow-hidden">

        {/* Search + filters */}
        <div className="flex flex-wrap items-center gap-2 px-4 py-2.5 border-b border-border">
          <div className="flex items-center gap-2 bg-muted/50 rounded-lg px-3 py-1.5 flex-1 min-w-[220px]">
            <Search className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search by name, username, IP, or extension..."
              className="flex-1 bg-transparent text-sm outline-none text-foreground placeholder:text-muted-foreground"
            />
            {search && (
              <button type="button" onClick={() => setSearch("")}>
                <X className="w-3 h-3 text-muted-foreground" />
              </button>
            )}
          </div>

          <div className="flex items-center gap-2">
            <span className="text-xs font-medium text-slate-600 dark:text-slate-400">Protocol</span>
            <div className="flex items-center gap-1 bg-muted rounded-lg p-1">
              <SegButton active={protocolFilter === "all"} onClick={() => setProtocolFilter("all")}>
                All
              </SegButton>
              <SegButton active={protocolFilter === "sip"} onClick={() => setProtocolFilter("sip")}>
                <Phone className="w-3.5 h-3.5" /> SIP
              </SegButton>
              <SegButton active={protocolFilter === "h323"} onClick={() => setProtocolFilter("h323")}>
                <Video className="w-3.5 h-3.5" /> H.323
              </SegButton>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <span className="text-xs font-medium text-slate-600 dark:text-slate-400">Status</span>
            <div className="flex items-center gap-1 bg-muted rounded-lg p-1">
              <SegButton active={statusFilter === "all"} onClick={() => setStatusFilter("all")}>
                All
              </SegButton>
              <SegButton active={statusFilter === "online"} onClick={() => setStatusFilter("online")}>
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> Online
              </SegButton>
              <SegButton active={statusFilter === "offline"} onClick={() => setStatusFilter("offline")}>
                <span className="w-1.5 h-1.5 rounded-full bg-slate-400" /> Offline
              </SegButton>
            </div>
          </div>
        </div>

        {/* Table */}
        {visible.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20">
            <Contact className="w-20 h-20 text-slate-200 dark:text-slate-700 mb-4" />
            <h4 className="text-base font-semibold text-slate-700 dark:text-white mb-1">
              {loading ? "Loading…" : endpoints.length === 0 ? "No accounts yet" : "No endpoints found"}
            </h4>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              {loading
                ? "Reading accounts from the gateway."
                : endpoints.length === 0
                  ? "Add a user to let an endpoint register with the gateway."
                  : "No account matches the current search and filters."}
            </p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50 dark:bg-card/80">
                <TableHead className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider w-[140px]">
                  Status
                </TableHead>
                <TableHead className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                  User
                </TableHead>
                <TableHead className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider w-[140px]">
                  Protocol
                </TableHead>
                <TableHead className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider w-[180px]">
                  IP Address
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {visible.map(e => (
                <TableRow key={e.id} className="hover:bg-slate-50 dark:hover:bg-slate-700/30">
                  <TableCell>
                    <StatusBadge online={e.online} />
                  </TableCell>
                  <TableCell>
                    {/* Name over username — often identical, and the second
                        line is what the endpoint authenticates as. */}
                    <div className="text-sm font-semibold text-slate-900 dark:text-white break-all">
                      {e.name}
                    </div>
                    <div className="text-xs text-slate-500 dark:text-slate-400 break-all">
                      {e.username}
                    </div>
                  </TableCell>
                  <TableCell>
                    <span className="inline-flex items-center gap-1.5 text-sm text-slate-700 dark:text-slate-300">
                      {e.protocol === "sip"
                        ? <><Phone className="w-3.5 h-3.5 text-slate-400" /> SIP</>
                        : <><Video className="w-3.5 h-3.5 text-slate-400" /> H.323</>}
                    </span>
                  </TableCell>
                  <TableCell className="text-sm font-mono text-slate-700 dark:text-slate-300">
                    {e.ipAddress}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      {/* Add New Account — the account an endpoint will authenticate against.
          Laid out with the same Dialog + SelectWrapper + Label/Input pieces
          the Telepresence add form uses, so it matches the rest of settings. */}
      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add New Account</DialogTitle>
          </DialogHeader>

          <p className="text-sm text-slate-500 dark:text-slate-400 -mt-2">
            Register a new H.323 or SIP account
          </p>

          <div className="space-y-4">
            {/* Protocol */}
            <SelectWrapper label="Protocol">
              <Select
                value={form.protocol}
                onValueChange={(value) => setForm((p) => ({ ...p, protocol: value as Protocol }))}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select protocol" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="sip">SIP</SelectItem>
                  <SelectItem value="h323">H.323</SelectItem>
                </SelectContent>
              </Select>
            </SelectWrapper>

            {/* Encrypted media — SIP only. This is the durable home for the
                policy: the dial-out dialog reads it, and its own switch is
                only a per-call override. */}
            {form.protocol === "sip" && (
              <SelectWrapper label="Media encryption">
                <Select
                  value={form.encryption}
                  onValueChange={(value) =>
                    setForm((p) => ({ ...p, encryption: value as AddForm["encryption"] }))
                  }
                >
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Not required (plain RTP)</SelectItem>
                    <SelectItem value="required">Required (SRTP)</SelectItem>
                  </SelectContent>
                </Select>
              </SelectWrapper>
            )}

            {/* Name */}
            <div className="space-y-2">
              <Label htmlFor="ab-name">Name</Label>
              <Input
                id="ab-name"
                value={form.name}
                maxLength={64}
                onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                placeholder="Enter display name"
              />
            </div>

            {/* Username / Extension */}
            <div className="space-y-2">
              <Label htmlFor="ab-username">Username / Extension *</Label>
              <Input
                id="ab-username"
                value={form.username}
                maxLength={64}
                onChange={(e) => { setForm((p) => ({ ...p, username: e.target.value })); setErrors({}) }}
                placeholder="e.g., 1001"
              />
              {errors.username && <p className="text-xs text-red-500">{errors.username}</p>}
            </div>

            {/* Password */}
            <div className="space-y-2">
              <Label htmlFor="ab-password">Password *</Label>
              <Input
                id="ab-password"
                type="password"
                value={form.password}
                maxLength={128}
                onChange={(e) => { setForm((p) => ({ ...p, password: e.target.value })); setErrors({}) }}
                placeholder="Enter password"
              />
              {errors.password && <p className="text-xs text-red-500">{errors.password}</p>}
            </div>
          </div>

          {/* Belongs to no single input — a protocol refusal, or the gateway
              being unreachable. */}
          {errors.form && <p className="text-xs text-red-500 mt-4">{errors.form}</p>}

          <div className="flex items-center justify-end gap-3 mt-6">
            <Button variant="outline" onClick={() => setShowAdd(false)}>
              Cancel
            </Button>
            {/* Disabled until both credentials are present — the endpoint
                cannot register without each of them. */}
            <Button
              onClick={() => { void handleAdd() }}
              disabled={saving || !form.username.trim() || !form.password.trim()}
            >
              {saving ? "Adding…" : "Add User"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}
